import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Sun, Wind, Shirt, Clock, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// VELAUREN Asset URLs
const ASSETS = {
  product: "https://customer-assets.emergentagent.com/job_elegant-soleil/artifacts/fj45fa45_1.png",
  hero: "https://customer-assets.emergentagent.com/job_elegant-soleil/artifacts/0s4ixjh8_velauren-polo-homme-resort-luxe-bord-de-mer.webp.png",
  detailCollar: "https://customer-assets.emergentagent.com/job_elegant-soleil/artifacts/clslt5kk_4.png",
  lifestyle: "https://customer-assets.emergentagent.com/job_elegant-soleil/artifacts/8wk0s214_2.png",
  labelDetail: "https://customer-assets.emergentagent.com/job_elegant-soleil/artifacts/ttxu2kha_6.png",
};

// Email Form Component
const EmailForm = ({ variant = "default", onSuccess }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, {
        email,
        first_name: null,
      });
      setSubmitted(true);
      setMessage("Inscription confirmée. Vous bénéficierez d'un accès prioritaire au lancement ainsi que de 20€ de bienvenue sur votre première commande.");
      
      // Meta Pixel - Lead event
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq('track', 'Lead');
      }
      
      if (onSuccess) onSuccess();
    } catch (error) {
      setMessage("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="success-enter" data-testid="subscription-success">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-velauren-navy flex items-center justify-center">
            <Check className="w-5 h-5 text-velauren-ivory" strokeWidth={1.5} />
          </div>
          <p className="font-serif text-2xl text-velauren-navy">Merci</p>
        </div>
        <p className="text-velauren-navy/70 text-base leading-relaxed">{message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full" data-testid="email-form">
      <div className="flex flex-col gap-5">
        <input
          type="email"
          placeholder="Votre adresse e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="input-velauren"
          data-testid="email-input"
        />
        <button
          type="submit"
          disabled={loading}
          className={`btn-velauren ${loading ? "opacity-70" : ""}`}
          data-testid="submit-button"
        >
          {loading ? "..." : "Rejoindre le lancement"}
        </button>
      </div>
      <p className="text-xs text-velauren-navy/50 mt-5 tracking-wide">
        {variant === "compact" 
          ? "Accès prioritaire au lancement. Aucun spam."
          : "Informations de lancement uniquement. Aucun envoi inutile."
        }
      </p>
      <p className="text-[11px] text-velauren-navy/40 mt-3 text-center tracking-wide">
        Les premières inscriptions sont en cours.
      </p>
    </form>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={ASSETS.hero}
          alt="Le Polo Riviera par VELAUREN"
          className="w-full h-full object-cover object-right"
        />
        <div className="hero-overlay-velauren absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 py-24 md:py-32 pl-6 md:pl-12 lg:pl-20 xl:pl-28">
        <div className="max-w-md">
          <p className="label-velauren mb-8 animate-fade-in opacity-0">
            Première pièce VELAUREN
          </p>
          
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-normal tracking-tight leading-[1.05] text-velauren-navy mb-4 animate-fade-in opacity-0 animate-delay-100">
            Le Polo Riviera.
          </h1>
          
          <p className="font-serif text-xl md:text-2xl text-velauren-navy mb-6 animate-fade-in opacity-0 animate-delay-100">
            Le polo pensé pour rester élégant au soleil.
          </p>
          
          <p className="text-base text-velauren-navy/70 leading-relaxed mb-8 animate-fade-in opacity-0 animate-delay-200">
            Une pièce élégante, respirante et discrètement protectrice, pensée pour 
            les heures où le soleil impose d'habitude un compromis.
          </p>
          
          <div className="flex flex-wrap gap-3 mb-10 animate-fade-in opacity-0 animate-delay-200">
            <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-velauren-navy/45 border-b border-velauren-navy/10">Maille micro-piquée</span>
            <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-velauren-navy/45 border-b border-velauren-navy/10">Respirant</span>
            <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-velauren-navy/45 border-b border-velauren-navy/10">Séchage rapide</span>
            <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-velauren-navy/45 border-b border-velauren-navy/10">Protection solaire</span>
          </div>

          <div className="bg-velauren-ivory/95 backdrop-blur-sm p-7 md:p-8 max-w-md animate-fade-in opacity-0 animate-delay-300">
            <p className="font-serif text-lg text-velauren-navy mb-7 leading-relaxed">
              Accès prioritaire au lancement + 20€ de bienvenue sur votre première commande
            </p>
            <EmailForm />
          </div>
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Shirt,
      title: "Allure maîtrisée",
      description: "Une ligne nette et une présence sobre, pensées pour rester élégantes naturellement.",
    },
    {
      icon: Wind,
      title: "Respirabilité légère",
      description: "Une maille micro-piquée légère, pensée pour accompagner les heures lumineuses sans lourdeur.",
    },
    {
      icon: Sun,
      title: "Protection solaire discrète",
      description: "Pensée pour accompagner les heures lumineuses sans compromettre l'allure.",
    },
    {
      icon: Clock,
      title: "Du matin au soir",
      description: "Une pièce pensée pour garder la même tenue, du premier café à la fin de journée.",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-velauren-ivory" data-testid="benefits-section">
      <div className="container-velauren">
        <div className="text-center mb-16">
          <p className="label-velauren mb-4">Les avantages</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal text-velauren-navy tracking-tight">
            Pensé pour rester juste au soleil
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="text-center"
              data-testid={`benefit-${index}`}
            >
              <benefit.icon
                className="w-6 h-6 mx-auto mb-5 text-velauren-navy/60"
                strokeWidth={1}
              />
              <h3 className="font-serif text-lg text-velauren-navy mb-3">
                {benefit.title}
              </h3>
              <p className="text-sm text-velauren-navy/55 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Approach Section
const ApproachSection = () => {
  return (
    <section className="section-spacing-lg bg-white" data-testid="approach-section">
      <div className="container-velauren">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <p className="label-velauren mb-5">Notre approche</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-velauren-navy mb-10 tracking-tight leading-[1.15]">
              Ni sport. Ni démonstratif.<br />Juste la bonne tenue.
            </h2>
            
            <div className="space-y-6 text-velauren-navy/65">
              <p className="leading-relaxed">
                La plupart des vêtements pensés pour le soleil sacrifient l'allure 
                au profit d'une technicité visible : matières trop sport, rendu 
                synthétique, coupes moins justes.
              </p>
              <p className="leading-relaxed">
                Le Polo Riviera prend une autre direction : offrir la tenue d'un polo 
                haut de gamme, avec la respirabilité, le confort et la protection 
                solaire discrète attendus pour les heures lumineuses.
              </p>
              <p className="leading-relaxed">
                Contrairement à un polo classique en coton, la maille du Polo Riviera 
                est pensée pour rester respirante et confortable même sous un soleil fort, 
                tout en conservant une allure élégante.
              </p>
              <p className="leading-relaxed text-velauren-navy font-medium">
                Le résultat : une pièce qui reste juste du déjeuner en terrasse 
                à la fin de journée, sans jamais perdre de sa tenue.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden">
              <img
                src={ASSETS.lifestyle}
                alt="Élégance resort VELAUREN"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Lifestyle Section
const LifestyleSection = () => {
  const contexts = [
    "Terrasse ensoleillée",
    "Marina",
    "Déjeuner extérieur",
    "Promenade",
    "Voyage",
    "Resort"
  ];

  return (
    <section className="py-16 md:py-24 bg-velauren-sand" data-testid="lifestyle-section">
      <div className="container-velauren">
        <div className="max-w-3xl mx-auto text-center">
          <p className="label-velauren mb-4">Un art de vivre</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal text-velauren-navy mb-8 tracking-tight">
            Pour chaque instant au soleil
          </h2>
          <div className="inline-flex flex-wrap justify-center gap-x-10 gap-y-3 mb-8">
            {contexts.map((context, index) => (
              <span 
                key={index} 
                className="py-2 text-[11px] text-velauren-navy/65 border-b border-velauren-navy/15 bg-transparent tracking-[0.18em] uppercase"
              >
                {context}
              </span>
            ))}
          </div>
          <p className="text-sm text-velauren-navy/60 leading-relaxed max-w-xl mx-auto">
            Une pièce qui garde sa tenue lorsque la chaleur monte, et son allure lorsque le moment compte.
          </p>
        </div>
      </div>
    </section>
  );
};

// Product Detail Section
const ProductDetailSection = () => {
  const details = [
    {
      title: "Col structuré",
      description: "Une ligne nette, ouverte ou fermée. Une présence propre, sans rigidité.",
    },
    {
      title: "Maille micro-piquée",
      description: "Texture fine, toucher mat, respirabilité naturelle.",
    },
    {
      title: "Patte épurée",
      description: "Trois boutons ton sur ton, proportions sobres et équilibrées.",
    },
    {
      title: "Protection solaire discrète",
      description: "Pensée pour accompagner les journées lumineuses sans basculer dans l'esthétique technique.",
    },
  ];

  return (
    <section className="section-spacing bg-white" data-testid="product-detail-section">
      <div className="container-velauren">
        <div className="text-center mb-14 pt-6">
          <p className="label-velauren mb-4">Le produit</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal text-velauren-navy tracking-tight">
            Les détails de la pièce
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: Detail Collar Image */}
          <div className="lg:col-span-5">
            <div className="overflow-hidden">
              <img
                src={ASSETS.detailCollar}
                alt="Détail du col Le Polo Riviera"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Center: Product Image with Signature Inset */}
          <div className="lg:col-span-3">
            <div className="overflow-hidden bg-velauren-sand/40 p-6">
              <img
                src={ASSETS.product}
                alt="Le Polo Riviera VELAUREN"
                className="w-full h-auto"
              />
            </div>
            {/* Signature Detail - small premium inset */}
            <div className="mt-4 relative">
              <div className="overflow-hidden w-3/4">
                <img
                  src={ASSETS.labelDetail}
                  alt="Signature VELAUREN"
                  className="w-full h-auto"
                />
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-velauren-navy/50 mt-3">
                Signature intérieure VELAUREN
              </p>
            </div>
          </div>

          {/* Right: Details List */}
          <div className="lg:col-span-4">
            <div className="space-y-6">
              {details.map((detail, index) => (
                <div key={index} className="border-l border-velauren-navy/10 pl-5">
                  <h3 className="font-serif text-base text-velauren-navy mb-1.5">
                    {detail.title}
                  </h3>
                  <p className="text-sm text-velauren-navy/55 leading-relaxed">
                    {detail.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Launch Section
const LaunchSection = () => {
  const points = [
    "Accès prioritaire à l'ouverture",
    "Première série réservée aux premiers inscrits",
    "20€ de bienvenue sur la première commande",
    "Informations de lancement uniquement",
  ];

  return (
    <section className="py-20 md:py-28 bg-velauren-navy text-velauren-ivory" data-testid="launch-section">
      <div className="container-velauren">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-velauren-ivory/50 mb-4">
            Le lancement
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal mb-10 tracking-tight">
            Pourquoi rejoindre le lancement
          </h2>

          <ul className="space-y-3 mb-10 text-left max-w-md mx-auto">
            {points.map((point, index) => (
              <li key={index} className="flex items-start gap-4">
                <span className="w-1 h-1 bg-velauren-gold rounded-full flex-shrink-0 mt-2.5" />
                <span className="text-velauren-ivory/75 leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>

          <p className="text-sm text-velauren-ivory/40 leading-relaxed max-w-lg mx-auto">
            Première série en préparation. Les premiers inscrits seront informés 
            en priorité de l'ouverture.
          </p>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="section-spacing-lg bg-velauren-ivory" data-testid="final-cta-section">
      <div className="container-velauren">
        <div className="max-w-xl mx-auto text-center">
          <p className="label-velauren mb-5">Dernière étape</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal text-velauren-navy mb-5 tracking-tight">
            Réservez votre accès au lancement
          </h2>
          <p className="text-velauren-navy/55 mb-10 leading-relaxed">
            Accès prioritaire à l'ouverture et 20€ de bienvenue sur votre première commande.
          </p>

          <div className="max-w-md mx-auto">
            <EmailForm variant="compact" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 border-t border-velauren-navy/5 bg-white" data-testid="footer">
      <div className="container-velauren">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <p className="font-serif text-2xl text-velauren-navy tracking-wide">VELAUREN</p>
            <p className="text-xs text-velauren-navy/55 mt-2 tracking-wide">
              Le polo solaire réinterprété.
            </p>
          </div>
          <p className="text-xs text-velauren-navy/50 tracking-wide">
            © 2026 VELAUREN. Première série en préparation.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-white" data-testid="app-container">
      <HeroSection />
      <BenefitsSection />
      <ApproachSection />
      <LifestyleSection />
      <ProductDetailSection />
      <LaunchSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}

export default App;
